// /web/src/lib/config.js

/**
 * Figure out which base URL we should use for API requests.
 *
 * Priority order:
 *   1. Explicit VITE_API_URL env var.
 *   2. If running in the browser on the public Pebbledash domains
 *      (app.pebbledash.co, www.pebbledash.co, or the bare apex), automatically
 *      swap the host for api.pebbledash.co so we don't try to POST to the
 *      static frontend.
 *   3. Fall back to the current origin (useful for local development with a
 *      proxy).
 */
const ABSOLUTE_URL_REGEX = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//;

export function getApiBaseUrl() {
  const envValue = ensureApiHost(normalizeBase(import.meta.env.VITE_API_URL));
  if (envValue) {
    return envValue;
  }

  if (typeof window === "undefined") {
    return "";
  }

  try {
    const { protocol, hostname, port } = window.location;
    return ensureApiHost(buildOrigin(protocol, hostname, port));
  } catch (error) {
    console.warn("Failed to infer API base URL", error);
    return "";
  }
}

function normalizeBase(base) {
  if (!base || typeof base !== "string") {
    return "";
  }
  const trimmed = base.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}

function shouldSwapToApiHost(hostname) {
  if (!hostname || typeof hostname !== "string") {
    return false;
  }

  const host = hostname.toLowerCase();
  if (host === "pebbledash.co") {
    return true;
  }

  if (!host.endsWith(".pebbledash.co")) {
    return false;
  }

  return host.startsWith("app.") || host.startsWith("www.");
}

function buildOrigin(protocol, hostname, port) {
  if (!hostname) {
    return "";
  }
  const portSuffix = port && port !== "80" && port !== "443" ? `:${port}` : "";
  return `${protocol}//${hostname}${portSuffix}`;
}

function ensureApiHost(baseUrl) {
  if (!baseUrl) {
    return "";
  }

  if (!ABSOLUTE_URL_REGEX.test(baseUrl)) {
    return baseUrl;
  }

  try {
    const parsed = new URL(baseUrl);
    if (shouldSwapToApiHost(parsed.hostname)) {
      const host = parsed.hostname.toLowerCase();
      const apiHost = host === "pebbledash.co" ? "api.pebbledash.co" : host.replace(/^[^.]+\./, "api.");
      const portSuffix = parsed.port && parsed.port !== "80" && parsed.port !== "443" ? `:${parsed.port}` : "";
      return `${parsed.protocol}//${apiHost}${portSuffix}`;
    }
  } catch {}

  return baseUrl;
}

export function appendApiPath(baseUrl, path) {
  const normalizedBase = normalizeBase(baseUrl);
  if (!normalizedBase) {
    return path;
  }
  return `${normalizedBase}${path}`;
}
